import { NotificationInfoModel } from './../../models/apis/system/notification/notification-info.model';
import {
    Component,
    Inject,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../../../environments/environment';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SidebarService } from '../../services/common/sidebar.service';
import { SidebarEnum } from '../../models/common/sidebar.enum';
import { AuthService } from '../../services/common/auth.service';
import { UserModel } from '../../models/apis/system/user/user.model';
import { AuthHospitalModel } from '../../models/common/auth-hospital.model';
import { DropdownModule } from 'primeng/dropdown';
import { SkeletonModule } from 'primeng/skeleton';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { UserService } from '../../services/apis/system/user.service';
import { NotificationService } from '../../services/apis/system/notification.service';
import { LoggerService } from '../../services/common/logger.service';
import { CommonConstants } from '../../constants/common.constants';
import { NotificationUnreadModel } from '../../models/apis/system/notification/notification-unread.model';
import { ToastModule } from 'primeng/toast';
import { TimeagoModule } from 'ngx-timeago';
import { AclsConstants } from '../../../shared/constants/acls.constants';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.css',
    standalone: true,
    imports: [
        TimeagoModule,
        AvatarModule,
        OverlayPanelModule,
        BadgeModule,
        CommonModule,
        ButtonModule,
        TooltipModule,
        ConfirmDialogModule,
        DropdownModule,
        SkeletonModule,
        FormsModule,
        ToastModule,
        RouterLink,
        TimeagoModule,
    ],
    providers: [MessageService, ConfirmationService],
})
export class NavbarComponent implements OnInit, OnDestroy {
    private readonly _confirmationService = inject(ConfirmationService);
    private readonly _sidebarService = inject(SidebarService);
    private readonly _oidcSecurityService = inject(OidcSecurityService);
    private readonly router = inject(Router);
    private readonly _authService = inject(AuthService);
    private readonly _userService = inject(UserService);
    private readonly _notificationService = inject(NotificationService);
    private readonly _errorLoggerService = inject(LoggerService);
    private readonly _messageService = inject(MessageService);

    private audio = new Audio();
    currentUser = signal<UserModel | null>(null);
    currentUserHospitals = signal<Array<AuthHospitalModel>>([]);
    currentSelectedHospital = signal<number | null>(null);
    notificationInfo = signal<NotificationInfoModel | null>(null);
    unreadNotifications = signal<NotificationUnreadModel[]>([]);
    fetchNotificationInfoLoading = signal<boolean>(false);
    fetchNotificationInfoIntervalLoading = signal<boolean>(false);
    fetchUnreadNotificationsLoading = signal<boolean>(false);
    currentNavbarBackgroundColor = signal<string>(
        CommonConstants.navbarBackgroundColorDefault,
    );
    currentNavbarForegroundColor = signal<string>(
        CommonConstants.navbarForegroundColorDefault,
    );

    allowViewProfile: boolean = false;

    minWidth: number = 800;
    isMobileState: boolean = innerWidth < this.minWidth;
    sidebarState: SidebarEnum = this.isMobileState
        ? SidebarEnum.mobileCollapsed
        : SidebarEnum.desktopLarge;
    intervalId: any = null;

    constructor() {
        this.audio.src = 'assets/sounds/alert.mp3';
        this.audio.load();
        this.setNavbarColors(
            localStorage.getItem(CommonConstants.navbarBackgroundColorKey) ??
                undefined,
            localStorage.getItem(CommonConstants.navbarForegroundColorKey) ??
                undefined,
        );
    }

    setNavbarColors(backgroundColor?: string, foregroundColor?: string): void {
        if (!backgroundColor) {
            backgroundColor = CommonConstants.navbarBackgroundColorDefault;
        }
        if (!foregroundColor) {
            foregroundColor = CommonConstants.navbarForegroundColorDefault;
        }
        this.currentNavbarBackgroundColor.set(backgroundColor);
        this.currentNavbarForegroundColor.set(foregroundColor);
        localStorage.setItem(
            CommonConstants.navbarBackgroundColorKey,
            backgroundColor,
        );
        localStorage.setItem(
            CommonConstants.navbarForegroundColorKey,
            foregroundColor,
        );
    }

    ngOnDestroy(): void {
        clearInterval(this.intervalId);
    }

    playSound(): void {
        this.audio.currentTime = 0; // rewind to start
        this.audio.play().catch((error) => {
            console.error('Failed to play notification sound', error);
        });
    }

    async setAcls() {
        this.allowViewProfile = await this._authService.isAuthorized(
            AclsConstants.USER.VIEW_PROFILE,
        );
    }

    async ngOnInit() {
        await this.setAcls();

        const user = await this._authService.getCurrentUser();
        if (user) {
            this.currentUser.set(user);

            const storedHospitalId = localStorage.getItem(
                CommonConstants.SelectedHospitalId,
            );
            if (storedHospitalId) {
                this.currentSelectedHospital.set(Number(storedHospitalId));
            } else if (user.userSetting?.defaultHospitalId) {
                this.currentSelectedHospital.set(user.userSetting.defaultHospitalId);
            }
        }

        const hospitals = await this._authService.getHospitals();
        this.currentUserHospitals.set(hospitals ?? []);
        if (this.currentSelectedHospital() && this.currentUserHospitals().length > 0) {
            const exists = this.currentUserHospitals().find(
                (h) => h.key === this.currentSelectedHospital(),
            );
            if (exists) {
                this.setNavbarColors(
                    exists.navbarBackgroundColor ?? undefined,
                    exists.navbarForegroundColor ?? undefined,
                );
            }
        }
        
        this._authService.userAccountObservable.subscribe((auth) => {
            if (auth) {
                this.currentUser.set(auth);
                const storedHospitalId = localStorage.getItem(
                    CommonConstants.SelectedHospitalId,
                );
                if (storedHospitalId) {
                    this.currentSelectedHospital.set(Number(storedHospitalId));
                } else if (auth.userSetting?.defaultHospitalId) {
                    this.currentSelectedHospital.set(
                        auth.userSetting.defaultHospitalId,
                    );
                }
            }
        });

        this._authService.userHospitalsObservable.subscribe((hospitals) => {
            if (hospitals) {
                this.currentUserHospitals.set(hospitals);
                if (this.currentSelectedHospital() && hospitals.length > 0) {
                    const exists = hospitals.find(
                        (h) => h.key === this.currentSelectedHospital(),
                    );
                    if (exists) {
                        this.setNavbarColors(
                            exists.navbarBackgroundColor ?? undefined,
                            exists.navbarForegroundColor ?? undefined,
                        );
                    }
                }
            }
        });

        this._sidebarService.sidebarStateObservable.subscribe({
            next: (state) => {
                this.sidebarState = state;
            },
        });
        this._sidebarService.isMobileResolutionObservable.subscribe({
            next: (isMobileState) => {
                this.isMobileState = isMobileState;
            },
        });
        this._authService.navbarBackgroundObservable.subscribe({
            next: (bgColor) => {
                this.currentNavbarBackgroundColor.set(bgColor);
            },
        });
        this._authService.navbarForegroundObservable.subscribe({
            next: (fgColor) => {
                this.currentNavbarForegroundColor.set(fgColor);
            },
        });
        this.fetchNotificationInfoLoading.set(true);
        this._notificationService
            .getInfo()
            .toPromise()
            .then((value) => {
                if (environment.isLocal) {
                    //console.log(value);
                }
                if (!value?.isError && value?.data) {
                    this.notificationInfo.set(value.data);
                    sessionStorage.setItem(
                        CommonConstants.notificationInfoSessionTicks,
                        value.data.ticks.toString(),
                    );
                    sessionStorage.setItem(
                        CommonConstants.notificationInfoSessionCount,
                        value.data.totalUnreads.toString(),
                    );
                    this.fetchUnreadNotifications();
                } else {
                    this._errorLoggerService.logError(
                        'Get Notification Info Error',
                        value?.errors ?? [],
                    );
                }
            })
            .catch((err) => {
                this._errorLoggerService.logError(
                    'Get Notification Info Error',
                    err,
                );
            })
            .finally(() => {
                this.fetchNotificationInfoLoading.set(false);
                this.fetchInterval();
            });
    }

    logout(): void {
        this._confirmationService.confirm({
            message: 'Are you sure want to sign-out?',
            header: 'Sign-out',
            icon: 'pi pi-sign-out',
            key: 'logoutConfirmDialog',
            closeOnEscape: false,
            acceptButtonStyleClass: 'p-button-primary p-button-text',
            rejectButtonStyleClass: 'p-button-danger p-button-text',
            accept: async () => {
                if (this.intervalId) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                }
                await this._authService.logout(false, true);
            },
        });
    }

    toggleSidebar() {
        if (
            this.isMobileState &&
            this.sidebarState == SidebarEnum.mobileCollapsed
        ) {
            this.sidebarState = SidebarEnum.mobileExpanded;
        } else if (
            this.isMobileState &&
            this.sidebarState == SidebarEnum.mobileExpanded
        ) {
            this.sidebarState = SidebarEnum.mobileCollapsed;
        } else if (
            !this.isMobileState &&
            this.sidebarState == SidebarEnum.desktopLarge
        ) {
            this.sidebarState = SidebarEnum.desktopSmall;
        } else if (
            !this.isMobileState &&
            this.sidebarState == SidebarEnum.desktopSmall
        ) {
            this.sidebarState = SidebarEnum.desktopLarge;
        }

        this._sidebarService.setState(this.sidebarState);
    }

    async onSelectedHospitalChange() {
        if (this.currentSelectedHospital()) {
            localStorage.setItem(
                CommonConstants.SelectedHospitalId,
                this.currentSelectedHospital()?.toString() ?? '',
            );

            this._authService.setSelectedHospital(
                this.currentSelectedHospital()!,
            );

            const selectedHospital = this.currentUserHospitals().find(
                (h) => h.key === this.currentSelectedHospital(),
            );

            if (selectedHospital) {
                this.setNavbarColors(
                    selectedHospital.navbarBackgroundColor ?? undefined,
                    selectedHospital.navbarForegroundColor ?? undefined,
                );
            }

            await this._userService
                .putDefaultHospital(this.currentSelectedHospital()!)
                .toPromise();
        }
    }

    redirectToUserProfile() {
        this.router.navigate(['user-profile']);
    }

    refreshSession() {
        this._oidcSecurityService.forceRefreshSession().subscribe((result) => {
            //console.log('Token refreshed:', result);
        });
    }

    checkNotificationChanges() {
        //for notification info
        if (!this.fetchNotificationInfoIntervalLoading()) {
            this.fetchNotificationInfoIntervalLoading.set(true);
            this._notificationService
                .getInfo()
                .toPromise()
                .then((value) => {
                    if (environment.isLocal) {
                        //console.log(value);
                    }
                    if (!value?.isError && value?.data) {
                        this.notificationInfo.set(value.data);
                        let currentSessionValue =
                            sessionStorage.getItem(
                                CommonConstants.notificationInfoSessionTicks,
                            ) ?? '';
                        //new notification
                        if (
                            currentSessionValue != value.data.ticks &&
                            value.data.ticks != '0'
                        ) {
                            sessionStorage.setItem(
                                CommonConstants.notificationInfoSessionTicks,
                                value.data.ticks,
                            );
                            this.fetchUnreadNotifications();
                            let notificationCount = Number.parseInt(
                                sessionStorage.getItem(
                                    CommonConstants.notificationInfoSessionCount,
                                ) ?? '0',
                            );
                            if (value.data.totalUnreads > notificationCount) {
                                this._messageService.add({
                                    severity: 'info',
                                    summary: 'New Notification',
                                    detail: "You've got new notification ☺️",
                                    key: 'newNotificationToast',
                                });
                                this.playSound();
                            } else if (
                                value.data.totalUnreads == notificationCount
                            ) {
                                this._messageService.add({
                                    severity: 'info',
                                    summary: 'Updated Notification',
                                    detail: "You've got notification update ☺️",
                                    key: 'newNotificationToast',
                                });
                                this.playSound();
                            }
                            sessionStorage.setItem(
                                CommonConstants.notificationInfoSessionCount,
                                value.data.totalUnreads.toString(),
                            );
                            this._notificationService.setLastNotificationInfo(
                                value.data,
                            );
                        }
                    } else {
                        this._errorLoggerService.logError(
                            'Get Notification Info Error',
                            value?.errors ?? [],
                        );
                    }
                })
                .catch((err) => {
                    console.error('Get Notification Info Error');
                    console.error(err);
                })
                .finally(() => {
                    this.fetchNotificationInfoIntervalLoading.set(false);
                });
        }
    }

    checkLastSessionChanges() {
        //for current session check
        this._userService
            .getUserLastToken()
            .toPromise()
            .then(async (value) => {
                if (environment.isLocal) {
                    //console.log(value);
                }
                if (value && !value?.isError) {
                    try {
                        await this._authService.checkUserLastToken(value.data);
                        this.checkNotificationChanges();
                    } catch (error) {
                        console.error('Error checking user last token');
                        console.error(error);
                    }
                }
            })
            .catch(async (err) => {
                console.error('Get User Last Token Error');
                console.error(err);

                if (err && err.status && err.status == 401) {
                    await this._authService.logout(false, true);
                }
            });
    }

    fetchInterval() {
        this.intervalId = setInterval(() => {
            this.checkLastSessionChanges();
        }, 5000);
    }

    fetchUnreadNotifications() {
        this.fetchUnreadNotificationsLoading.set(true);
        this._notificationService
            .getUnreads()
            .toPromise()
            .then((value) => {
                if (environment.isLocal) {
                    //console.log(value);
                }
                if (!value?.isError && value?.data) {
                    this.unreadNotifications.set(value.data);
                }
            })
            .catch((err) => {
                console.error('Fetching unread notifications produced error');
                console.error(err);
            })
            .finally(() => {
                this.fetchUnreadNotificationsLoading.set(false);
            });
    }

    markAsRead(notification: NotificationUnreadModel) {
        if (notification.loading) return;
        notification.loading = true;
        this._notificationService
            .setRead(notification.id)
            .toPromise()
            .then((value) => {
                if (!value?.isError) {
                    notification.isRead = true;
                } else {
                    this._errorLoggerService.logError(
                        'Mark As Read Failed',
                        value?.errors ?? [],
                    );
                }
            })
            .catch((err) => {
                this._errorLoggerService.logError('Mark As Read Failed', err);
            })
            .finally(() => {
                notification.loading = false;
            });
    }

    removeNotification(notification: NotificationUnreadModel) {
        if (notification.loading) return;
        this._confirmationService.confirm({
            message: `Are you sure want to remove this notification?`,
            header: 'Remove Notification',
            icon: 'fa fa-exclamation-triangle',
            closeOnEscape: false,
            key: 'deleteNotificationConfirmDialog',
            acceptButtonStyleClass: 'p-button-primary p-button-raised',
            rejectButtonStyleClass: 'p-button-danger p-button-raised',
            accept: () => {
                notification.loading = true;
                this._notificationService
                    .delete(notification.id)
                    .toPromise()
                    .then((value) => {
                        if (!value?.isError) {
                            this.unreadNotifications.set(
                                this.unreadNotifications().filter(
                                    (c) => c.id != notification.id,
                                ),
                            );
                        } else {
                            this._errorLoggerService.logError(
                                'Issue with Remove Notification',
                                value?.errors ?? [],
                            );
                        }
                    })
                    .catch((err) => {
                        this._errorLoggerService.logError(
                            'Issue with Remove Notification',
                            err,
                        );
                    })
                    .finally(() => {
                        notification.loading = false;
                    });
            },
        });
    }

    markAsReadAll() {
        this._confirmationService.confirm({
            message: `Mark all notifications as read?`,
            header: 'Mark As Read Notifications',
            icon: 'fa fa-exclamation-triangle',
            closeOnEscape: false,
            key: 'markReadConfirmDialog',
            acceptButtonStyleClass: 'p-button-primary p-button-raised',
            rejectButtonStyleClass: 'p-button-danger p-button-raised',
            accept: () => {
                this._notificationService
                    .setReadAll()
                    .toPromise()
                    .then((value) => {
                        if (!value?.isError) {
                            this.unreadNotifications.set([]);
                            this.notificationInfo.set({
                                ...this.notificationInfo(),
                                totalUnreads: 0,
                            } as NotificationInfoModel);
                        } else {
                            this._errorLoggerService.logError(
                                'Mark As Read All Failed',
                                value?.errors ?? [],
                            );
                        }
                    })
                    .catch((err) => {
                        this._errorLoggerService.logError(
                            'Mark As Read All Failed',
                            err,
                        );
                    });
            },
        });
    }

    getDownLoadLink(notification: NotificationUnreadModel) {
        if (notification.loading) return;
        notification.loading = true;
        this._notificationService
            .getDownloadLink(notification.id)
            .toPromise()
            .then((value) => {
                if (!value?.isError && value?.data) {
                    window.open(value.data, '_blank');
                } else {
                    this._errorLoggerService.logError(
                        'Issue with Download Document',
                        value?.errors ?? [],
                    );
                }
            })
            .catch((err) => {
                this._errorLoggerService.logError(
                    'Issue with Download Document',
                    err,
                );
            })
            .finally(() => {
                notification.loading = false;
            });
    }

    showNotification(op: OverlayPanel, event: any) {
        if (this.isMobileState) {
            this.router.navigate(['system', 'notifications']);
        } else {
            op.toggle(event);
        }
    }

    navigateToDetail(url: string) {
        if (url) {
            this.router.navigateByUrl(url);
        }
    }
}
